import { useQuery } from '@tanstack/react-query';
import { useDataProvider } from 'react-admin';
import { useRegion } from '../auth/RegionContext';
import { buildTicketInboxView, type TicketInboxView } from './ticketInboxModel';
import type { Ticket, User } from '../dataProvider/types';

/**
 * The Ticket inbox, read through the aggregation seam (`buildTicketInboxView`), NOT
 * computed in the component. For v1 it pulls the raw tickets and users from the mock
 * provider and aggregates client-side; the real-data migration replaces the fetch-and-
 * aggregate here with a server-side endpoint and the surface does not change. Same seam
 * idiom as useChargebacks.
 *
 * OPERATIONAL, SWITCHER-SCOPED (D-ticket-inbox): scope comes from the region context
 * (UK | US | GLOBAL), exactly like the chargebacks and cashout queues. A single-region
 * support agent sees only their market's tickets. The injected `now` drives the SLA-
 * breach derivation.
 */

const ALL = {
  pagination: { page: 1, perPage: 100_000 },
  sort: { field: 'id', order: 'ASC' as const },
  filter: {},
};

export const useTickets = (
  now: number,
): {
  view: TicketInboxView | null;
  isLoading: boolean;
} => {
  const dataProvider = useDataProvider();
  const { region } = useRegion();

  const { data, isLoading } = useQuery({
    queryKey: ['tickets', region, now],
    queryFn: async () => {
      const [tickets, users] = await Promise.all([
        dataProvider.getList('tickets', ALL),
        dataProvider.getList('users', ALL),
      ]);
      return buildTicketInboxView(
        {
          tickets: tickets.data as unknown as Ticket[],
          users: users.data as unknown as User[],
        },
        { scope: region, now },
      );
    },
  });

  return { view: data ?? null, isLoading };
};
